// ============ TYPE DEFINITIONS ============

/** Quill Delta operation */
interface DeltaOp {
    insert: string | Record<string, unknown>
    attributes?: Record<string, unknown>
}

/** Quill Delta document */
interface DeltaJson {
    ops: DeltaOp[]
}

/** ProseMirror mark */
interface PmMark {
    type: string
    attrs?: Record<string, unknown>
}

/**
 * ProseMirror node - compatible with TipTap's JSONContent
 * Using optional `type` to match JSONContent interface
 */
interface PmNode {
    type?: string
    attrs?: Record<string, unknown>
    marks?: PmMark[]
    content?: PmNode[]
    text?: string
}

/** Intermediate block representation during parsing */
interface ParsedBlock {
    type: string
    ops: DeltaOp[]
    listType?: string
    indent?: number
    level?: number
    /** Whether this block is also a blockquote */
    isBlockquote?: boolean
    /** Whether this block is also a code block */
    isCodeBlock?: boolean
}

/** List stack entry for nested list tracking */
interface ListStackEntry {
    type: string
    node: PmNode
    indent: number
}

/** Answer object that may contain delta_json */
interface Answer {
    delta_json?: DeltaJson
    content?: string
}

/** Mention data in Quill format */
interface QuillMention {
    id: string
    value?: string
    content?: string
    type?: string
    denotationChar?: string
    link?: string
    kind?: string
    previewUrl?: string | null
    url?: string | null
    contentType?: string | null
}

/** Mention attrs in ProseMirror format */
interface PmMentionAttrs {
    id: string
    label?: string
    content?: string
    type?: string
    denotationChar?: string
    link?: string
    kind?: string
    previewUrl?: string | null
    url?: string | null
    contentType?: string | null
    buttonId?: string
    instanceId?: string
}

interface PmActionTokenAttrs {
    tag?: string
    ai_action_id?: string
    actionId?: string
    promptContent?: string
    prompt_content?: string
    promptPlacement?: string
    prompt_placement?: string
    promptPlaceholder?: boolean
    supportsPromptInput?: boolean
    supports_prompt_input?: boolean
    displayName?: string
    display_name?: string
    actionDefinitionId?: string
    action_definition_id?: string
    kind?: string
    type?: string
    aiModelId?: string
    ai_model_id?: string
    toolKey?: string
    tool_key?: string
    [key: string]: unknown
}

// ============ UTILITY FUNCTIONS ============

const HTTP_LINK_REGEX = /^https?:\/\//i
const MEME_LINK_PREFIX = '@v2quest_self_'

function isPersistedLink(value: unknown): value is string {
    return (
        typeof value === 'string' &&
        (HTTP_LINK_REGEX.test(value) || value.startsWith(MEME_LINK_PREFIX))
    )
}

/** Clean up insert null bug for entity quests */
const cleanDeltaJson = (deltaJson: DeltaJson | null): DeltaJson | null => {
    if (deltaJson && deltaJson.ops) {
        const newDeltaJson = { ...deltaJson, ops: [...deltaJson.ops] }

        for (let i = 0; i < newDeltaJson.ops.length; i++) {
            const op = newDeltaJson.ops[i]
            if (op?.insert === null) {
                newDeltaJson.ops[i] = { insert: '' }
            }
        }
        return newDeltaJson
    }
    return deltaJson
}

const removeTrailingNewlines = (deltaJson: DeltaJson | null): DeltaJson | null => {
    if (!deltaJson) return null

    const newDeltaJson = { ...deltaJson, ops: [...deltaJson.ops] }
    const ops = newDeltaJson.ops

    if (ops) {
        // Cleanup trailing blank inserts
        let lastInsert = ops[ops.length - 1]?.insert
        while (typeof lastInsert === 'string' && lastInsert.trim() === '') {
            ops.pop()
            lastInsert = ops[ops.length - 1]?.insert
        }

        // Cleanup trailing newlines in non-blank insert string
        if (typeof lastInsert === 'string') {
            ops.pop()
            ops.push({ insert: lastInsert.trimEnd() })
        }
    }
    return newDeltaJson
}

const getValue = (answer: Answer | null): DeltaJson | string | null => {
    if (answer?.delta_json) {
        return cleanDeltaJson({ ...answer.delta_json })
    }
    return answer?.content ?? null
}

interface GetTextOptions {
    wrapUsernames?: boolean
    excludeActionTokens?: boolean
    excludeActionTags?: boolean
}

/**
 * Extracts text from a Delta JSON object, optionally wrapping usernames.
 */
const getText = (
    deltaJson: DeltaJson | string | null,
    options: GetTextOptions = {},
): string | null => {
    if (!deltaJson) return null
    // If it's already a string, return it
    if (typeof deltaJson === 'string') return deltaJson

    const { wrapUsernames = false, excludeActionTokens = options.excludeActionTags || false } =
        options
    const ops = deltaJson.ops || []

    return ops
        .filter(op => {
            const insert = op.insert
            if (excludeActionTokens && op.attributes?.actionTagAttrs) return false
            return (
                typeof insert === 'string' ||
                typeof insert === 'number' ||
                (typeof insert === 'object' && insert !== null && 'mention' in insert)
            )
        })
        .map(op => {
            const insert = op.insert as string | number | { mention: QuillMention }

            if (typeof insert === 'object' && 'mention' in insert) {
                const mention = insert.mention
                if (mention.type === 'model') {
                    return `@@${mention.content}`
                }
                // BACKWARD COMPATIBILITY: old mentions had denotationChar as empty string and content with leading '@'
                let username = mention.content || mention.value || ''
                if (username.startsWith('@')) {
                    username = username.substring(1)
                }
                return username ? (wrapUsernames ? `@[${username}]` : `@${username}`) : ''
            }

            return String(insert || '')
        })
        .join('')
        .trim()
}

/**
 * Counts "lines" in a Quill Delta or plain string.
 *
 * For Quill deltas, we treat newline characters (`\n`) as line boundaries.
 * Quill typically includes a trailing newline for paragraphs, so the line count
 * is effectively the number of newline chars, with a minimum of 1 when any content exists.
 */
const getLineCount = (value: DeltaJson | string | null): number => {
    if (!value) return 0

    // Plain string
    if (typeof value === 'string') {
        const matches = value.match(/\n/g)
        const newlines = matches ? matches.length : 0
        return Math.max(1, newlines)
    }

    // Delta JSON
    const ops = value.ops || []
    let newlines = 0
    let sawAnyContent = false

    for (const op of ops) {
        const insert = op?.insert
        if (typeof insert === 'string') {
            sawAnyContent = sawAnyContent || insert.length > 0
            const matches = insert.match(/\n/g)
            if (matches) newlines += matches.length
        } else if (insert != null) {
            // embed counts as content
            sawAnyContent = true
        }
    }

    if (!sawAnyContent) return 0
    return Math.max(1, newlines)
}

const getImages = (deltaJson: DeltaJson | null): string[] | null => {
    if (!deltaJson) return null

    const ops = deltaJson.ops || []
    const images = ops
        .filter(op => typeof op.insert === 'object' && op.insert !== null && 'image' in op.insert)
        .map(op => (op.insert as { image: string }).image)

    return images.length > 0 ? images : null
}

const isEmpty = (deltaJson: DeltaJson | null): boolean => {
    try {
        if (!deltaJson) return true

        const ops = deltaJson.ops || []
        if (ops.length === 0) return true

        // Check for model mentions - if found, content is not empty
        const hasModelMention = ops.some(op => {
            const insert = op.insert
            return (
                typeof insert === 'object' &&
                insert !== null &&
                'mention' in insert &&
                (insert as { mention: QuillMention }).mention?.denotationChar === '@@'
            )
        })
        if (hasModelMention) return false

        // If only one op, check if it's truly empty
        if (ops.length === 1) {
            const insert = ops[0].insert
            if (typeof insert === 'string') return insert.trim() === ''
            if (typeof insert === 'object' && insert !== null) return false
        }

        return false
    } catch (e) {
        console.error('Error in isEmpty check:', e)
        return true
    }
}

/**
 * Checks if a delta contains a model mention
 */
const containsModelMention = (delta: DeltaJson | null): boolean => {
    if (!delta?.ops) return false

    return delta.ops.some(op => {
        const insert = op.insert
        if (typeof insert === 'object' && insert !== null && 'mention' in insert) {
            return (insert as { mention: QuillMention }).mention?.type === 'model'
        }
        return false
    })
}

interface ModelMention {
    id: string
    value: string
    content: string
    denotationChar: string
    type: string
    link: string
    isModel: boolean
}

const createModelMention = (model: string): ModelMention => ({
    id: model,
    value: model,
    content: model,
    denotationChar: '@@',
    type: 'model',
    link: `/models/${model}`,
    isModel: true,
})

/* =========================================================
 *  Quill Delta  <->  ProseMirror JSON  Converter
 * ---------------------------------------------------------
 *  Data-driven converter between Quill Delta and TipTap/ProseMirror.
 *
 *  Supported formats:
 *    • paragraphs, headings (1-6), blockquotes, code blocks
 *    • nested lists (bullet/ordered with indent)
 *    • horizontal rules
 *    • marks: bold, italic, underline, strike, code, link
 *    • embeds: image, video, mention
 * =======================================================*/

// ============ CONFIGURATION MAPS ============

/** Quill attribute → ProseMirror mark type */
const MARK_MAP: Record<string, string> = {
    bold: 'bold',
    italic: 'italic',
    underline: 'underline',
    strike: 'strike',
    code: 'code',
}

const ACTION_PROMPT_SENTINEL = '\u00a0'
const ACTION_PROMPT_PLACEHOLDER = 'Prompt'

/**
 * Extract all block info from Quill attributes.
 * Quill can have compound attributes (e.g., list + blockquote = blockquote inside list item)
 */
function getBlockInfo(attrs: Record<string, unknown> | undefined): Partial<ParsedBlock> | null {
    if (!attrs) return null

    const info: Partial<ParsedBlock> = {}
    let hasBlockAttr = false

    // Check for list (primary container)
    if (attrs.list) {
        info.type = 'list'
        info.listType = attrs.list as string
        info.indent = (attrs.indent as number) || 0
        hasBlockAttr = true
    }

    // Check for heading
    if (attrs.header) {
        info.type = 'heading'
        info.level = attrs.header as number
        hasBlockAttr = true
    }

    // Check for blockquote (can be standalone or inside list)
    if (attrs.blockquote) {
        if (!info.type) {
            info.type = 'blockquote'
        } else {
            info.isBlockquote = true
        }
        hasBlockAttr = true
    }

    // Check for code-block (can be standalone or inside list)
    if (attrs['code-block']) {
        if (!info.type) {
            info.type = 'code-block'
        } else {
            info.isCodeBlock = true
        }
        hasBlockAttr = true
    }

    return hasBlockAttr ? info : null
}

// ============ SHARED HELPERS ============

/** Convert Quill attributes → ProseMirror marks array */
function attrsToMarks(attrs: Record<string, unknown> | undefined): PmMark[] | undefined {
    if (!attrs) return undefined
    const marks: PmMark[] = []

    if (isPersistedLink(attrs.link)) {
        marks.push({ type: 'link', attrs: { href: attrs.link } })
    }
    for (const [quillAttr, pmMark] of Object.entries(MARK_MAP)) {
        if (attrs[quillAttr]) marks.push({ type: pmMark })
    }

    return marks.length ? marks : undefined
}

/** Convert ProseMirror marks → Quill attributes */
function marksToAttrs(marks: PmMark[] | undefined): Record<string, unknown> | undefined {
    if (!marks?.length) return undefined
    const attrs: Record<string, unknown> = {}

    for (const mark of marks) {
        if (mark.type === 'link') {
            if (isPersistedLink(mark.attrs?.href)) {
                attrs.link = mark.attrs?.href
            }
        } else if (mark.type !== 'actionPrompt') {
            attrs[mark.type] = true
        }
    }

    return Object.keys(attrs).length ? attrs : undefined
}

/** Create a mention PM node from Quill mention data */
function createMentionNode(mention: QuillMention): PmNode {
    return {
        type: 'mention',
        attrs: {
            id: mention.id,
            label: mention.content || mention.value || mention.id,
            type: mention.type || 'user',
            denotationChar: mention.denotationChar || '@',
            content: mention.content || mention.value || mention.id,
            link:
                mention.link ||
                (mention.type === 'model' ? `/models/${mention.id}` : `/users/${mention.id}`),
            ...(mention.kind !== undefined ? { kind: mention.kind } : {}),
            ...(mention.previewUrl !== undefined ? { previewUrl: mention.previewUrl } : {}),
            ...(mention.url !== undefined ? { url: mention.url } : {}),
            ...(mention.contentType !== undefined ? { contentType: mention.contentType } : {}),
        },
    }
}

function actionTokenPromptContent(promptContent: unknown): string {
    return typeof promptContent === 'string'
        ? promptContent
              .replace(new RegExp(ACTION_PROMPT_SENTINEL, 'g'), '')
              .replace(/\u200b/g, '')
              .replace(/\u00a0/g, ' ')
              .replace(/[\r\n]+/g, ' ')
              .trim()
        : ''
}

function actionTokenEditorPromptText(
    attrs: Record<string, unknown> | undefined,
    promptContent: string,
): string {
    if (attrs?.promptPlaceholder === true) return ACTION_PROMPT_SENTINEL
    if (promptContent) return `${promptContent}${ACTION_PROMPT_SENTINEL}`

    const supportsPromptInput =
        typeof attrs?.supportsPromptInput === 'boolean'
            ? attrs.supportsPromptInput
            : typeof attrs?.supports_prompt_input === 'boolean'
            ? attrs.supports_prompt_input
            : true

    return supportsPromptInput === false ? '' : ACTION_PROMPT_SENTINEL
}

function actionTokenPromptContentFromMark(
    promptContent: unknown,
    markAttrs?: Record<string, unknown>,
): string {
    const text = actionTokenPromptContent(promptContent)
    if (markAttrs?.promptPlaceholder === true && text === ACTION_PROMPT_PLACEHOLDER) return ''
    return text
}

function plainTextFromPmContent(content: PmNode[] | undefined): string {
    if (!content?.length) return ''
    return content
        .map(node => {
            if (node.type === 'text') return node.text || ''
            return plainTextFromPmContent(node.content)
        })
        .join('')
}

function actionIdForAttrs(attrs: Record<string, unknown> | undefined): string | null {
    const actionId =
        typeof attrs?.ai_action_id === 'string'
            ? attrs.ai_action_id
            : typeof attrs?.actionId === 'string'
            ? attrs.actionId
            : null
    return actionId?.trim() || null
}

function actionPromptMarkMatchesToken(
    markAttrs: Record<string, unknown> | undefined,
    tokenAttrs: Record<string, unknown> | undefined,
): boolean {
    const tokenActionId = actionIdForAttrs(tokenAttrs)
    const markActionId = actionIdForAttrs(markAttrs)
    if (tokenActionId && markActionId && tokenActionId !== markActionId) return false
    return true
}

function createActionTokenNode(attrs: Record<string, unknown>): PmNode {
    const rawTag = typeof attrs.tag === 'string' ? attrs.tag : ''
    const tag = rawTag.replace(/^!+/, '')
    const aiActionId =
        typeof attrs.ai_action_id === 'string'
            ? attrs.ai_action_id
            : typeof attrs.actionId === 'string'
            ? attrs.actionId
            : null
    const rawPromptContent =
        typeof attrs.promptContent === 'string'
            ? attrs.promptContent
            : typeof attrs.prompt_content === 'string'
            ? attrs.prompt_content
            : null
    const promptContent = actionTokenPromptContent(rawPromptContent)
    const editorPromptText = actionTokenEditorPromptText(attrs, promptContent)
    const promptPlacement =
        attrs.promptPlacement === 'before' || attrs.prompt_placement === 'before' ? 'before' : null
    const promptPlaceholder = attrs.promptPlaceholder === true
    const displayName =
        typeof attrs.displayName === 'string'
            ? attrs.displayName
            : typeof attrs.display_name === 'string'
            ? attrs.display_name
            : null
    const supportsPromptInput =
        typeof attrs.supportsPromptInput === 'boolean'
            ? attrs.supportsPromptInput
            : typeof attrs.supports_prompt_input === 'boolean'
            ? attrs.supports_prompt_input
            : null
    const actionDefinitionId =
        typeof attrs.actionDefinitionId === 'string'
            ? attrs.actionDefinitionId
            : typeof attrs.action_definition_id === 'string'
            ? attrs.action_definition_id
            : null
    const kind =
        typeof attrs.kind === 'string'
            ? attrs.kind
            : typeof attrs.type === 'string'
            ? attrs.type
            : null
    const aiModelId =
        typeof attrs.aiModelId === 'string'
            ? attrs.aiModelId
            : typeof attrs.ai_model_id === 'string'
            ? attrs.ai_model_id
            : null
    const toolKey =
        typeof attrs.toolKey === 'string'
            ? attrs.toolKey
            : typeof attrs.tool_key === 'string'
            ? attrs.tool_key
            : null

    return {
        type: 'actionTag',
        attrs: {
            tag,
            ...(aiActionId ? { ai_action_id: aiActionId } : {}),
            ...(promptContent ? { promptContent } : {}),
            ...(promptPlacement ? { promptPlacement } : {}),
            ...(promptPlaceholder ? { promptPlaceholder } : {}),
            ...(supportsPromptInput === false ? { supportsPromptInput } : {}),
            ...(displayName ? { displayName } : {}),
            ...(actionDefinitionId ? { actionDefinitionId } : {}),
            ...(kind ? { kind } : {}),
            ...(aiModelId ? { aiModelId } : {}),
            ...(toolKey ? { toolKey } : {}),
        },
        ...(editorPromptText ? { content: [{ type: 'text', text: editorPromptText }] } : {}),
    }
}

function createActionTokenInlineContent(attrs: Record<string, unknown>): PmNode[] {
    const tokenNode = createActionTokenNode(attrs)
    return [tokenNode]
}

function createActionTokenDelta(attrs: PmActionTokenAttrs): DeltaOp {
    const rawTag = typeof attrs.tag === 'string' ? attrs.tag : ''
    const tag = rawTag.replace(/^!+/, '')
    const commandText = tag ? `!${tag}` : ''
    const aiActionId = attrs.ai_action_id || attrs.actionId || null
    const promptContent = attrs.promptContent || attrs.prompt_content || null
    const promptPlacement =
        attrs.promptPlacement === 'before' || attrs.prompt_placement === 'before' ? 'before' : null
    const promptPlaceholder = attrs.promptPlaceholder === true
    const supportsPromptInput =
        typeof attrs.supportsPromptInput === 'boolean'
            ? attrs.supportsPromptInput
            : typeof attrs.supports_prompt_input === 'boolean'
            ? attrs.supports_prompt_input
            : null
    const displayName = attrs.displayName || attrs.display_name || null
    const actionDefinitionId = attrs.actionDefinitionId || attrs.action_definition_id || null
    const kind = attrs.kind || attrs.type || null
    const aiModelId = attrs.aiModelId || attrs.ai_model_id || null
    const toolKey = attrs.toolKey || attrs.tool_key || null

    return {
        insert: commandText,
        attributes: {
            bold: true,
            actionTagAttrs: {
                tag,
                ...(aiActionId ? { ai_action_id: aiActionId } : {}),
                ...(promptContent ? { promptContent } : {}),
                ...(promptPlacement ? { promptPlacement } : {}),
                ...(promptPlaceholder ? { promptPlaceholder } : {}),
                ...(supportsPromptInput === false ? { supportsPromptInput } : {}),
                ...(displayName ? { displayName } : {}),
                ...(actionDefinitionId ? { actionDefinitionId } : {}),
                ...(kind ? { kind } : {}),
                ...(aiModelId ? { aiModelId } : {}),
                ...(toolKey ? { toolKey } : {}),
            },
        },
    }
}

/** Create Quill mention data from PM mention node */
function createMentionDelta(attrs: PmMentionAttrs): { mention: QuillMention } {
    return {
        mention: {
            id: attrs.id,
            value: attrs.label || attrs.content || attrs.id,
            content: attrs.content || attrs.label || attrs.id,
            type: attrs.type || 'user',
            denotationChar: attrs.denotationChar || '@',
            link:
                attrs.link ||
                (attrs.type === 'model' ? `/models/${attrs.id}` : `/users/${attrs.id}`),
            ...(attrs.kind !== undefined ? { kind: attrs.kind } : {}),
            ...(attrs.previewUrl !== undefined ? { previewUrl: attrs.previewUrl } : {}),
            ...(attrs.url !== undefined ? { url: attrs.url } : {}),
            ...(attrs.contentType !== undefined ? { contentType: attrs.contentType } : {}),
        },
    }
}

// ============ DELTA → PROSEMIRROR ============

/** Convert Quill ops to PM inline content (text, mentions, etc.) */
function opsToInlineContent(ops: DeltaOp[]): PmNode[] {
    const content: PmNode[] = []

    for (const op of ops) {
        if (typeof op.insert === 'string') {
            if (op.attributes?.actionTagAttrs) {
                content.push(
                    ...createActionTokenInlineContent(
                        op.attributes.actionTagAttrs as Record<string, unknown>,
                    ),
                )
                continue
            }

            if (op.attributes?.bold && /^![A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*$/.test(op.insert)) {
                content.push(
                    createActionTokenNode({
                        tag: op.insert,
                    }),
                )
                continue
            }

            if (!op.insert) continue

            const node: PmNode = { type: 'text', text: op.insert }
            const marks = attrsToMarks(op.attributes)
            if (marks) node.marks = marks
            content.push(node)
        } else if (typeof op.insert === 'object' && op.insert !== null) {
            if ('mention' in op.insert) {
                const node = createMentionNode(op.insert.mention as QuillMention)
                const marks = attrsToMarks(op.attributes)
                if (marks) node.marks = marks
                content.push(node)
            }
        }
    }

    return content
}

/** Parse Quill ops into intermediate block representation */
function parseOpsToBlocks(ops: DeltaOp[]): ParsedBlock[] {
    const blocks: ParsedBlock[] = []
    let currentBlock: ParsedBlock = { type: 'paragraph', ops: [] }

    for (let i = 0; i < ops.length; i++) {
        const op = ops[i]
        const insert = op.insert

        // Handle embed objects (image, video, divider)
        if (typeof insert === 'object' && insert !== null) {
            if ('divider' in insert || 'hr' in insert) {
                // Check if this HR is inside a list by looking at the next newline
                const nextOp = ops[i + 1]
                const nextAttrs = nextOp?.insert === '\n' ? nextOp.attributes : undefined
                const nextBlockInfo = getBlockInfo(nextAttrs as Record<string, unknown> | undefined)

                // Flush current block, add HR with list context if applicable
                if (currentBlock.ops.length > 0) {
                    blocks.push(currentBlock)
                    currentBlock = { type: 'paragraph', ops: [] }
                }

                const hrBlock: ParsedBlock = { type: 'horizontalRule', ops: [] }
                if (nextBlockInfo?.type === 'list') {
                    // HR is inside a list item - carry the list info
                    hrBlock.listType = nextBlockInfo.listType
                    hrBlock.indent = nextBlockInfo.indent
                }
                blocks.push(hrBlock)
                if (nextOp?.insert === '\n') {
                    i += 1
                }
            } else {
                currentBlock.ops.push(op)
            }
            continue
        }

        if (typeof insert !== 'string') continue

        // Handle newline (block terminator in Quill)
        if (insert === '\n') {
            const blockInfo = getBlockInfo(op.attributes)
            if (blockInfo) {
                Object.assign(currentBlock, blockInfo)
            }
            blocks.push(currentBlock)
            currentBlock = { type: 'paragraph', ops: [] }
            continue
        }

        // Handle text with potential embedded newlines
        const lines = insert.split('\n')
        for (let j = 0; j < lines.length; j++) {
            if (lines[j]) {
                currentBlock.ops.push({ ...op, insert: lines[j] })
            }
            if (j < lines.length - 1) {
                blocks.push(currentBlock)
                currentBlock = { type: 'paragraph', ops: [] }
            }
        }
    }

    // Don't forget trailing content
    if (currentBlock.ops.length > 0 || blocks.length === 0) {
        blocks.push(currentBlock)
    }

    return blocks
}

/** Convert parsed blocks to ProseMirror document */
function blocksToPmDoc(blocks: ParsedBlock[]): PmNode {
    const doc: PmNode = { type: 'doc', content: [] }

    // Accumulators for multi-line blocks
    let codeLines: string[] = []
    let quoteParas: PmNode[] = []
    let listStack: ListStackEntry[] = []

    const flush = (): void => {
        if (codeLines.length) {
            const text = codeLines.join('\n')
            doc.content!.push({
                type: 'codeBlock',
                ...(text ? { content: [{ type: 'text', text }] } : {}),
            })
            codeLines = []
        }
        if (quoteParas.length) {
            doc.content!.push({ type: 'blockquote', content: quoteParas })
            quoteParas = []
        }
        if (listStack.length) {
            doc.content!.push(listStack[0].node)
            listStack = []
        }
    }

    /** Add a list item with the given inner content node */
    const addListItemWithContent = (block: ParsedBlock, innerNode: PmNode): void => {
        const indent = block.indent || 0
        const listType = block.listType === 'bullet' ? 'bulletList' : 'orderedList'

        const listItem: PmNode = {
            type: 'listItem',
            content: [innerNode],
        }

        if (!listStack.length) {
            const newList: PmNode = { type: listType, content: [listItem] }
            listStack.push({ type: listType, node: newList, indent: 0 })
            return
        }

        // Adjust stack to target indent
        while (listStack.length > indent + 1) listStack.pop()

        if (indent === 0) {
            if (listStack[0].type === listType) {
                listStack[0].node.content!.push(listItem)
            } else {
                flush()
                const newList: PmNode = { type: listType, content: [listItem] }
                listStack.push({ type: listType, node: newList, indent: 0 })
            }
            return
        }

        // Create intermediate levels if needed
        while (listStack.length <= indent) {
            const parent = listStack[listStack.length - 1]
            const parentItems = parent.node.content!
            let lastItem = parentItems[parentItems.length - 1]
            if (!lastItem) {
                lastItem = { type: 'listItem', content: [] }
                parentItems.push(lastItem)
            }
            const nested: PmNode = { type: listType, content: [] }
            lastItem.content!.push(nested)
            listStack.push({ type: listType, node: nested, indent: listStack.length })
        }

        listStack[indent].node.content!.push(listItem)
    }

    /** Add a list item, building inner content based on block type */
    const addListItem = (block: ParsedBlock): void => {
        const inlineContent = opsToInlineContent(block.ops)

        let innerNode: PmNode
        if (block.isBlockquote) {
            innerNode = {
                type: 'blockquote',
                content: inlineContent.length
                    ? [{ type: 'paragraph', content: inlineContent }]
                    : [],
            }
        } else if (block.isCodeBlock) {
            const text = block.ops
                .map(op => (typeof op.insert === 'string' ? op.insert : ''))
                .join('')
            innerNode = {
                type: 'codeBlock',
                content: text ? [{ type: 'text', text }] : [],
            }
        } else {
            innerNode = inlineContent.length
                ? { type: 'paragraph', content: inlineContent }
                : { type: 'paragraph', content: [] }
        }

        addListItemWithContent(block, innerNode)
    }

    for (let i = 0; i < blocks.length; i++) {
        const block = blocks[i]
        const next = blocks[i + 1]

        switch (block.type) {
            case 'list':
                if (codeLines.length || quoteParas.length) flush()
                addListItem(block)
                break

            case 'code-block': {
                if (quoteParas.length || listStack.length) flush()
                const text = block.ops
                    .map(op => (typeof op.insert === 'string' ? op.insert : ''))
                    .join('')
                codeLines.push(text)
                if (!next || next.type !== 'code-block') flush()
                break
            }

            case 'blockquote': {
                if (codeLines.length || listStack.length) flush()
                const content = opsToInlineContent(block.ops)
                if (content.length) quoteParas.push({ type: 'paragraph', content })
                if (!next || next.type !== 'blockquote') flush()
                break
            }

            case 'heading': {
                flush()
                const content = opsToInlineContent(block.ops)
                doc.content!.push({
                    type: 'heading',
                    attrs: { level: block.level || 1 },
                    content,
                })
                break
            }

            case 'horizontalRule':
                if (block.listType) {
                    // HR inside a list item
                    if (codeLines.length || quoteParas.length) flush()
                    addListItemWithContent(block, { type: 'horizontalRule' })
                } else {
                    flush()
                    doc.content!.push({ type: 'horizontalRule' })
                }
                break

            default: {
                // Paragraph - check for top-level embeds
                flush()
                const para: PmNode = { type: 'paragraph', content: [] }

                for (const op of block.ops) {
                    const insert = op.insert
                    if (typeof insert === 'object' && insert !== null) {
                        if ('image' in insert) {
                            if (para.content!.length) {
                                doc.content!.push({ ...para, content: [...para.content!] })
                                para.content = []
                            }
                            doc.content!.push({
                                type: 'image',
                                attrs: { src: (insert as { image: string }).image },
                            })
                        } else if ('video' in insert) {
                            if (para.content!.length) {
                                doc.content!.push({ ...para, content: [...para.content!] })
                                para.content = []
                            }
                            doc.content!.push({
                                type: 'video',
                                attrs: { src: (insert as { video: string }).video },
                            })
                        } else {
                            para.content!.push(...opsToInlineContent([op]))
                        }
                    } else {
                        para.content!.push(...opsToInlineContent([op]))
                    }
                }

                if (para.content!.length || block.ops.length === 0) doc.content!.push(para)
            }
        }
    }

    flush()
    if (!doc.content!.length) doc.content!.push({ type: 'paragraph', content: [] })
    return doc
}

function deltaToPmJson(deltaJson: DeltaJson | null): PmNode {
    if (!deltaJson?.ops?.length) {
        return { type: 'doc', content: [{ type: 'paragraph', content: [] }] }
    }
    const blocks = parseOpsToBlocks(deltaJson.ops)
    return blocksToPmDoc(blocks)
}

// ============ PROSEMIRROR → DELTA ============

/** Process PM inline content → Delta ops */
function inlineContentToOps(content: PmNode[] | undefined, ops: DeltaOp[]): void {
    if (!content) return

    for (let index = 0; index < content.length; index++) {
        const node = content[index]
        switch (node.type) {
            case 'text': {
                const actionPromptMark = node.marks?.find(mark => mark.type === 'actionPrompt')
                const nextNode = content[index + 1]
                if (
                    actionPromptMark &&
                    nextNode?.type === 'actionTag' &&
                    actionPromptMarkMatchesToken(
                        actionPromptMark.attrs,
                        nextNode.attrs as Record<string, unknown>,
                    )
                ) {
                    const attrs = { ...(nextNode.attrs as PmActionTokenAttrs) }
                    const normalizedPromptContent = actionTokenPromptContentFromMark(
                        node.text || attrs.promptContent || attrs.prompt_content,
                        actionPromptMark.attrs,
                    )
                    if (normalizedPromptContent) {
                        attrs.promptContent = normalizedPromptContent
                    } else {
                        delete attrs.promptContent
                        delete attrs.prompt_content
                    }
                    delete attrs.promptPlaceholder
                    ops.push(createActionTokenDelta(attrs))
                    index += 1
                    break
                }

                const attrs = marksToAttrs(node.marks)
                const op: DeltaOp = { insert: node.text || '' }
                if (attrs) op.attributes = attrs
                ops.push(op)
                break
            }
            case 'mention':
                {
                    const op: DeltaOp = {
                        insert: createMentionDelta(node.attrs as unknown as PmMentionAttrs),
                    }
                    const attrs = marksToAttrs(node.marks)
                    if (attrs) op.attributes = attrs
                    ops.push(op)
                }
                break
            case 'actionTag':
                {
                    const attrs = { ...(node.attrs as PmActionTokenAttrs) }
                    const contentPromptContent = actionTokenPromptContent(
                        plainTextFromPmContent(node.content),
                    )

                    let promptContent = ''
                    let promptIndex = index + 1
                    while (promptIndex < content.length) {
                        const promptNode = content[promptIndex]
                        if (promptNode.type !== 'text') break

                        const promptMark = promptNode.marks?.find(
                            mark =>
                                mark.type === 'actionPrompt' &&
                                actionPromptMarkMatchesToken(mark.attrs, attrs),
                        )
                        if (!promptMark) break

                        promptContent += promptNode.text || ''
                        if (promptMark.attrs?.promptPlaceholder === true) {
                            attrs.promptPlaceholder = true
                        }
                        promptIndex += 1
                    }

                    const normalizedPromptContent = actionTokenPromptContentFromMark(
                        contentPromptContent ||
                            promptContent ||
                            attrs.promptContent ||
                            attrs.prompt_content,
                        attrs,
                    )
                    if (normalizedPromptContent) {
                        attrs.promptContent = normalizedPromptContent
                        if (attrs.promptPlaceholder !== true) delete attrs.promptPlaceholder
                    } else {
                        delete attrs.promptContent
                        delete attrs.prompt_content
                        delete attrs.promptPlaceholder
                    }
                    if (promptIndex > index + 1) {
                        index = promptIndex - 1
                    }
                    ops.push(createActionTokenDelta(attrs))
                }
                break
            case 'hardBreak':
                ops.push({ insert: '\n' })
                break
        }
    }
}

/** Process PM list node recursively */
function listToOps(node: PmNode, ops: DeltaOp[], indent = 0): void {
    const listType = node.type === 'bulletList' ? 'bullet' : 'ordered'

    for (const item of node.content || []) {
        if (item.type !== 'listItem') continue

        for (const child of item.content || []) {
            if (child.type === 'paragraph') {
                inlineContentToOps(child.content, ops)
                const attrs: Record<string, unknown> = { list: listType }
                if (indent > 0) attrs.indent = indent
                ops.push({ insert: '\n', attributes: attrs })
            } else if (child.type === 'bulletList' || child.type === 'orderedList') {
                listToOps(child, ops, indent + 1)
            } else if (child.type === 'blockquote') {
                // Blockquote inside list item
                for (const quoteChild of child.content || []) {
                    if (quoteChild.type === 'paragraph') {
                        inlineContentToOps(quoteChild.content, ops)
                        const attrs: Record<string, unknown> = { list: listType, blockquote: true }
                        if (indent > 0) attrs.indent = indent
                        ops.push({ insert: '\n', attributes: attrs })
                    }
                }
            } else if (child.type === 'codeBlock') {
                // Code block inside list item
                const text = (child.content || [])
                    .filter(c => c.type === 'text')
                    .map(c => c.text || '')
                    .join('')
                for (const line of text.split('\n')) {
                    ops.push({ insert: line })
                    const attrs: Record<string, unknown> = { list: listType, 'code-block': true }
                    if (indent > 0) attrs.indent = indent
                    ops.push({ insert: '\n', attributes: attrs })
                }
            } else if (child.type === 'horizontalRule') {
                // HR inside list item
                ops.push({ insert: { divider: true } })
                const attrs: Record<string, unknown> = { list: listType }
                if (indent > 0) attrs.indent = indent
                ops.push({ insert: '\n', attributes: attrs })
            }
        }
    }
}

function pmJsonToDelta(pmJson: PmNode | null): DeltaJson {
    const ops: DeltaOp[] = []
    if (!pmJson?.content) return { ops: [{ insert: '' }] }

    for (const node of pmJson.content) {
        switch (node.type) {
            case 'paragraph':
                if (!node.content?.length) {
                    ops.push({ insert: '\n' })
                } else {
                    inlineContentToOps(node.content, ops)
                    ops.push({ insert: '\n' })
                }
                break

            case 'heading':
                inlineContentToOps(node.content, ops)
                ops.push({
                    insert: '\n',
                    attributes: { header: (node.attrs?.level as number) || 1 },
                })
                break

            case 'blockquote':
                for (const child of node.content || []) {
                    if (child.type === 'paragraph') {
                        inlineContentToOps(child.content, ops)
                        ops.push({ insert: '\n', attributes: { blockquote: true } })
                    }
                }
                break

            case 'codeBlock': {
                const text = (node.content || [])
                    .filter(c => c.type === 'text')
                    .map(c => c.text || '')
                    .join('')
                for (const line of text.split('\n')) {
                    ops.push({ insert: line })
                    ops.push({ insert: '\n', attributes: { 'code-block': true } })
                }
                break
            }

            case 'bulletList':
            case 'orderedList':
                listToOps(node, ops, 0)
                break

            case 'horizontalRule':
                ops.push({ insert: { divider: true } })
                ops.push({ insert: '\n' })
                break

            case 'image':
                ops.push({ insert: { image: node.attrs?.src } })
                ops.push({ insert: '\n' })
                break

            case 'video':
                ops.push({ insert: { video: node.attrs?.src } })
                ops.push({ insert: '\n' })
                break
        }
    }

    // Trim trailing plain newlines
    while (ops.length && ops[ops.length - 1].insert === '\n' && !ops[ops.length - 1].attributes) {
        ops.pop()
    }

    return { ops: ops.length ? ops : [{ insert: '' }] }
}

export {
    getText,
    getLineCount,
    getImages,
    getValue,
    isEmpty,
    removeTrailingNewlines,
    containsModelMention,
    createModelMention,
    deltaToPmJson,
    pmJsonToDelta,
}

// Export types for consumers
export type { DeltaJson, DeltaOp, PmNode, PmMark }
